/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ISearchData, ISearchFormViewProps, ISearchViewProps } from '@msdyn365-commerce-modules/search';
import { Button, format, isMobile, Module, Node, VariantType } from '@msdyn365-commerce-modules/utilities';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ISearchProps } from '../definition-extensions/search.ext.props.autogenerated';
import { CategorySuggestionsComponent } from './components/search.categorysuggest';
import { FormComponent } from './components/search.form';
import { KeywordSuggestionsComponent } from './components/search.keywordsuggest';
import { ProductSuggestionsComponent } from './components/search.productsuggest';
import SearchModelsAction, { SearchModelsInput } from '../../../actions/search-models.action';
import { QueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { IAriServiceModelExtension } from '../../../actions/DataServiceEntities.g';
import * as _ from 'lodash';
import { AriSuggestionsComponent } from './components/search.arisuggest';
// import MsDyn365, { getUrlSync } from "@msdyn365-commerce/core";
/**
 * Render Title for AutoSuggest.
 * @param searchSuggestionHeading -The heading resource string.
 * @param searchText -The search string.
 * @returns -The JSX Element.
 */
const renderTitle = (searchSuggestionHeading: string, searchText: string): JSX.Element => {
    const searchTextString = '"'.concat(searchText.concat('"'));
    const searchAutoSuggestionHeading = searchSuggestionHeading
        ? format(searchSuggestionHeading, searchTextString)
        : `Search for ${searchTextString}`;

    return <Node className='msc-autoSuggest__search-title'>{searchAutoSuggestionHeading}</Node>;
};

/**
 * Search View.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const SearchView: React.FC<ISearchViewProps & ISearchProps<ISearchData>> = (
    props: ISearchViewProps & ISearchProps<ISearchData>
): JSX.Element => {
    const {
        Search,
        AutoSuggestAriaLabel,
        AutoSuggestAriaLabelText,
        searchText,
        AutoSuggest,
        KeywordSuggest,
        ProductSuggest,
        UlKeyword,
        UlProduct,
        form,
        autosuggestKeyword,
        autosuggestProduct,
        SearchForm,
        FormWrapper,
        label,
        context,
        isLoadingAutoSuggest,
        isLoadingNode,
        context: {
            request: { apiSettings },
            actionContext
        },
        config: { topResultsCount }
    } = props;

    const { searchSuggestionHeading, categorySuggestionHeading, keywordsHeading, noResultText, clearSearchButtonText } = props.resources;

    const [searchTextString, setSearchTextString] = useState<string>();
    const [isMobilePort, setIsMobile] = useState<boolean>(false);
    const [ariSearchResults, setAriSearchResults] = useState<IAriServiceModelExtension[]>([]);
    const [ariSearchLoading, setAriSearchLoading] = useState<boolean>(false);

    const debouncedSearchHandler = useMemo(() => _.debounce((search: string) => getAriSearchResults(search), 500), []);

    useEffect(() => {
        setSearchTextString(searchText);
        if (searchText.length > 0) debouncedSearchHandler(searchText);
    }, [searchText]);

    useEffect(() => {
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [debouncedSearchHandler]);

    /**
     * ClearSearch callback method to clear search string.
     */
    const clearSearch = (): void => {
        setSearchTextString('');

        const propsForm = props.form as ISearchFormViewProps;
        const propsFormInput = propsForm.input as React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
        const propsFormInputCurrent = (propsFormInput.ref as React.RefObject<HTMLInputElement>).current;

        if (propsFormInputCurrent) {
            propsFormInputCurrent.value = '';
        }

        propsFormInput.value = '';
        props.searchText = '';
        props.FormWrapper.action = '';
    };

    const isMobileViewport = useCallback(() => {
        const isMobileView = isMobile({ variant: VariantType.Browser, context: context.request }) === 'xs';
        setIsMobile(isMobileView);
    }, [context]);

    useEffect(() => {
        window.addEventListener('resize', isMobileViewport);
        isMobileViewport();
    }, [isMobileViewport]);

    const viewport = isMobile({ variant: VariantType.Browser, context: context.request });

    const isMobileView = viewport === 'xs';

    AutoSuggest.className = !StringExtensions.isNullOrEmpty(searchTextString)
        ? `${AutoSuggest.className} show`
        : `${AutoSuggest.className} hide`;
    const isSearchText: boolean = !StringExtensions.isNullOrEmpty(searchTextString);

    const getAriSearchResults = async (search: string) => {
        setAriSearchLoading(true);
        const queryResultSettings: QueryResultSettings = {
            Paging: {
                Top: topResultsCount || 5
            }
        };
        const input = new SearchModelsInput(search, queryResultSettings, 'Search', apiSettings, false);

        const data = await SearchModelsAction(input, actionContext);
        setAriSearchResults(data.ariSearchResults.Models || []);
        setAriSearchLoading(false);
    };

    // const  _generateSeeMoreUrl = (): string => {
    //     const {
    //         context: {
    //             actionContext,
    //             app: {
    //                 config: { searchQueryStringParameter }
    //             }
    //         }
    //     } = props;

    //     const query = props.searchText;
    //     const searchQsp = searchQueryStringParameter || 'q';
    //     const searchURL = _appendQueryParams(getUrlSync('search', actionContext) || '', query, searchQsp);
    //     return searchURL;
    // };

    // const _appendQueryParams = (route: string, query: string, qsp: string): string => {
    //     if (!MsDyn365.isBrowser) {
    //         return '';
    //     }
    //     const queryUrl = new URL(route, window.location.href);
    //     if (qsp && query) {
    //         queryUrl.searchParams.set(qsp, query);
    //     }

    //     return queryUrl.href;
    // };

    return (
        <Module {...Search}>
            {label}
            {FormComponent(
                form as ISearchFormViewProps,
                SearchForm,
                FormWrapper,
                isMobilePort,
                clearSearchButtonText,
                props,
                isSearchText,
                clearSearch
            )}
            {!isMobileView && (
                <Node {...AutoSuggest}>
                    {!StringExtensions.isNullOrEmpty(searchTextString) ? (
                        <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node>
                    ) : (
                        ''
                    )}
                    {!StringExtensions.isNullOrEmpty(searchTextString) ? (
                        <>
                            <Node className='msc-autoSuggest__search-title-container'>
                                <Button
                                    className='ms-search__form-cancelSearch'
                                    aria-label={props.resources.cancelBtnAriaLabel}
                                    onClick={clearSearch}
                                    type='button'
                                />
                                {renderTitle(searchSuggestionHeading, searchText)}
                            </Node>
                            <Node className='msc-autoSuggest__results-container'>
                                <Node className='msc-autoSuggest__results'>
                                    {ProductSuggestionsComponent(
                                        ProductSuggest,
                                        UlProduct,
                                        props,
                                        autosuggestProduct,
                                        isLoadingAutoSuggest,
                                        isLoadingNode
                                    )}

                                    {CategorySuggestionsComponent(
                                        searchTextString,
                                        categorySuggestionHeading,
                                        noResultText,
                                        props,
                                        isLoadingAutoSuggest,
                                        isLoadingNode
                                    )}
                                    {AriSuggestionsComponent(noResultText, props, ariSearchLoading, ariSearchResults, isLoadingNode)}
                                    {KeywordSuggestionsComponent(
                                        KeywordSuggest,
                                        UlKeyword,
                                        keywordsHeading,
                                        noResultText,
                                        autosuggestKeyword,
                                        isLoadingAutoSuggest,
                                        isLoadingNode
                                    )}
                                </Node>
                            </Node>
                            {/* <a href={_generateSeeMoreUrl() || '#'} className={'see-more-button'}>See More</a> */}
                        </>
                    ) : null}
                </Node>
            )}
            {isMobileView && props.isSearchFormExpanded && (
                <Node {...AutoSuggest}>
                    {!StringExtensions.isNullOrEmpty(searchTextString) ? (
                        <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node>
                    ) : (
                        ''
                    )}
                    {!StringExtensions.isNullOrEmpty(searchTextString) ? (
                        <>
                            <Node className='msc-autoSuggest__search-title-container'>
                                {(form as ISearchFormViewProps).cancelBtn}
                                {renderTitle(searchSuggestionHeading, searchText)}
                            </Node>
                            <Node className='msc-autoSuggest__results-container'>
                                <Node className='msc-autoSuggest__results'>
                                    {CategorySuggestionsComponent(
                                        searchTextString,
                                        categorySuggestionHeading,
                                        noResultText,
                                        props,
                                        isLoadingAutoSuggest,
                                        isLoadingNode
                                    )}
                                    {AriSuggestionsComponent(noResultText, props, ariSearchLoading, ariSearchResults, isLoadingNode)}
                                    {KeywordSuggestionsComponent(
                                        KeywordSuggest,
                                        UlKeyword,
                                        keywordsHeading,
                                        noResultText,
                                        autosuggestKeyword,
                                        isLoadingAutoSuggest,
                                        isLoadingNode
                                    )}
                                </Node>
                                {ProductSuggestionsComponent(
                                    ProductSuggest,
                                    UlProduct,
                                    props,
                                    autosuggestProduct,
                                    isLoadingAutoSuggest,
                                    isLoadingNode
                                )}
                            </Node>
                        </>
                    ) : null}
                </Node>
            )}
        </Module>
    );
};
export default SearchView;
